/**
 * lex.ivoliq Anwalts-Dashboard (Sep 2026).
 * Aufbau: Zu erledigen (To-dos inkl. UVA-QR & Direkt-Aktionen) → Wo Sie stehen (Jahres-Kennzahlen)
 * → einheitliche Status-Boxen → UVA-Verlauf.
 */
import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { FileText, ReceiptText, ShieldCheck, Check, Minus, CircleAlert, Send } from "lucide-react";
import LexUvaPanel from "@/components/lex/LexUvaPanel";
import { LexKammerBanner } from "@/components/lex/LexKammerBanner";
import "@/styles/lex.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtEur = (v) =>
  v == null ? "—" : new Intl.NumberFormat("de-AT", { style: "currency", currency: "EUR" }).format(v);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("de-AT", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return "—"; }
};
const fmtDateShort = (iso) => (iso ? new Date(iso).toLocaleDateString("de-AT") : "—");
const today = () => new Date().toISOString().slice(0, 10);

/* Einheitliche Status-Box: Label + Icon, EIN Hauptwert, genau 3 Zeilen, Button unten auf gleicher Höhe */
const StatusBox = ({ label, icon: Icon, dark = false, value, sub, rows, action, testid, fade }) => (
  <div className={`p-8 flex flex-col ${fade} ${dark ? "text-white" : "lex-card"}`}
    style={dark ? { background: "#0A1128" } : {}} data-testid={testid}>
    <div className="flex items-center justify-between mb-5">
      <span className={`text-xs uppercase tracking-widest font-semibold ${dark ? "text-slate-400" : "text-slate-500"}`}>{label}</span>
      <Icon className="h-4 w-4" style={{ color: "#C5A059" }} />
    </div>
    <div className="min-h-[72px]">
      {value}
      {sub && <p className={`text-xs mt-1 ${dark ? "text-slate-400" : "text-slate-500"}`}>{sub}</p>}
    </div>
    <div className="mt-5 space-y-2 text-sm">
      {rows.map(([k, v], i) => (
        <div key={i} className={`flex justify-between items-center gap-3 border-t pt-2 ${dark ? "border-white/10" : "border-slate-100"}`}>
          <span className={dark ? "text-slate-400" : "text-slate-500"}>{k}</span>
          <span className="text-right">{v}</span>
        </div>
      ))}
    </div>
    <div className="mt-auto pt-6">{action}</div>
  </div>
);

const StatTile = ({ label, value, sub, testid }) => (
  <div className="lex-card p-5" data-testid={testid}>
    <p className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">{label}</p>
    <p className="lex-mono text-xl font-semibold mt-1.5 truncate" style={{ color: "#0A1128" }}>{value}</p>
    <p className="text-xs text-slate-400 mt-0.5 truncate">{sub || "\u00A0"}</p>
  </div>
);

const uvaChipStyle = (r) => {
  if (r.status === "paid") return { background: "#ECFDF5", color: "#047857", border: "1px solid #A7F3D0" };
  if (r.zahllast > 0) return { background: "#FBF7EF", color: "#8A6D3B", border: "1px solid #C5A059" };
  return { background: "#F8FAFC", color: "#64748B", border: "1px solid #E2E8F0" };
};
const uvaChipMark = (r) => (r.status === "paid" ? "✓" : r.zahllast > 0 ? "⏳" : "—");
const uvaStatusLabel = (r) =>
  r.status === "paid" ? "Bezahlt" : r.zahllast > 0 ? "Zahlung offen" : "Keine Zahllast";

export default function LexDashboard() {
  const { user, refreshUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [dash, setDash] = useState(null);
  const [uvaRuns, setUvaRuns] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [paidForId, setPaidForId] = useState(null);
  const [invPaidDate, setInvPaidDate] = useState(today());

  const load = useCallback(() => {
    return Promise.all([
      axios.get(`${API}/lex/status`).then((r) => setStatus(r.data)).catch(() => {}),
      axios.get(`${API}/lex/dashboard`).then((r) => setDash(r.data)).catch(() => {}),
      axios.get(`${API}/lex/uva/current`).then((r) => setUvaRuns(r.data.runs || [])).catch(() => setUvaRuns([])),
    ]);
  }, []);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load, user?.onboarding_completed]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="animate-spin h-10 w-10 border-b-2" style={{ borderColor: "#0A1128" }} />
      </div>
    );
  }

  const vm = status?.vollmachten || {};
  const rec = status?.recurring;
  const honorar = status?.honorar || {};
  const todos = dash?.todos || {};
  const ob = todos.onboarding || { completed: true };
  const pending = todos.honorar_pending || [];
  const unpaid = todos.honorar_unpaid || [];
  const uvaOpen = (uvaRuns || []).filter((r) => r.status === "approved" && r.zahllast > 0);
  const stats = dash?.year_stats || {};
  const history = dash?.uva_history || [];
  const lastUva = history[0] || null;

  const todoCount = uvaOpen.length + pending.length + unpaid.length + (ob.completed ? 0 : 1);

  const sendHonorar = async (inv) => {
    setBusy(`send-${inv.id}`);
    try {
      await axios.post(`${API}/lex/honorar/${inv.id}/send`);
      toast.success(`Honorarnote ${inv.invoice_number} freigegeben und an die Kanzlei gesendet.`);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Versand fehlgeschlagen.");
    } finally {
      setBusy(null);
    }
  };

  const markInvoicePaid = async (inv) => {
    setBusy(`paid-${inv.id}`);
    try {
      await axios.put(`${API}/invoices/${inv.id}`, {
        status: "paid",
        paid_at: new Date(`${invPaidDate}T12:00:00Z`).toISOString(),
      });
      toast.success(`Note ${inv.invoice_number} als bezahlt markiert (${fmtDateShort(invPaidDate)}).`);
      setPaidForId(null);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Konnte nicht als bezahlt markiert werden.");
    } finally {
      setBusy(null);
    }
  };

  const remindKanzlei = async (inv) => {
    setBusy(`remind-${inv.id}`);
    try {
      const res = await axios.post(`${API}/lex/honorar/${inv.id}/remind`);
      toast.success(`Freundliche Erinnerung an ${res.data.sent_to} gesendet.`);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erinnerung konnte nicht gesendet werden.");
    } finally {
      setBusy(null);
    }
  };

  const resumeOnboarding = async () => {
    setBusy("onboarding");
    try {
      await axios.post(`${API}/lex/onboarding/skip`, { skipped: false });
      await refreshUser();
    } catch {
      toast.error("Konnte den Einrichtungs-Assistenten nicht öffnen.");
    } finally {
      setBusy(null);
    }
  };

  const TodoRow = ({ icon, title, sub, actions, testid }) => (
    <div className="lex-card p-5 flex items-center justify-between gap-4 flex-wrap" style={{ borderLeft: "3px solid #C5A059" }} data-testid={testid}>
      <div className="flex items-start gap-3 min-w-[240px]">
        {icon}
        <div>
          <p className="text-sm font-semibold" style={{ color: "#0A1128" }}>{title}</p>
          <p className="text-xs text-slate-500 mt-0.5">{sub}</p>
        </div>
      </div>
      <div className="flex items-end gap-3 flex-wrap">{actions}</div>
    </div>
  );

  return (
    <div data-testid="lex-dashboard">
      {/* Kopfzeile — dynamisch je nach To-dos */}
      <div className="mb-10 lex-fade-up">
        <div className="lex-gold-line mb-4" />
        <h1 className="lex-serif text-4xl sm:text-5xl font-semibold" style={{ color: "#0A1128" }}>
          Guten Tag{status?.name ? `, ${status.name}` : ""}.
        </h1>
        <p className="text-sm text-slate-500 mt-2" data-testid="lex-dash-subtitle">
          {todoCount > 0
            ? <>{todoCount === 1 ? "Ein Punkt wartet" : `${todoCount} Punkte warten`} auf Sie — alles andere übernimmt Ihre Steuerberatung.</>
            : "Alles erledigt. Ihre Steuerberatung übernimmt UVA, Pauschalierung und Fristen — Sie haben null Aufwand."}
        </p>
      </div>

      <LexKammerBanner />

      {/* Zu erledigen */}
      <div className="mb-10 lex-fade-up-1" data-testid="lex-dash-todos">
        <p className="text-xs uppercase tracking-widest font-semibold mb-4" style={{ color: "#C5A059" }}>Zu erledigen</p>
        {todoCount === 0 ? (
          <div className="lex-card p-5 flex items-center gap-3" data-testid="lex-dash-all-done">
            <Check className="h-5 w-5 flex-shrink-0" style={{ color: "#047857" }} />
            <p className="text-sm text-slate-600">Nichts zu tun — Honorarnoten und UVA laufen automatisch.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {!ob.completed && (
              <TodoRow
                testid="lex-todo-onboarding"
                icon={<CircleAlert className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: "#C5A059" }} />}
                title="Einrichtung unvollständig"
                sub={`${ob.steps_done} von ${ob.steps_total} Schritten erledigt — Honorarnoten und UVA starten erst nach Abschluss.`}
                actions={
                  <button onClick={resumeOnboarding} disabled={busy === "onboarding"} className="lex-btn text-xs"
                    data-testid="lex-todo-onboarding-btn">
                    Jetzt fertigstellen
                  </button>
                }
              />
            )}

            {/* Offene UVA-Zahlungen: QR-Code + Bezahlt-Aktion direkt hier */}
            {uvaOpen.length > 0 && (
              <LexUvaPanel onlyOpen runs={uvaRuns} onChanged={load} />
            )}

            {pending.map((inv) => (
              <TodoRow
                key={inv.id}
                testid={`lex-todo-approve-${inv.invoice_number}`}
                icon={<Send className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: "#C5A059" }} />}
                title={`Honorarnote ${inv.invoice_number} wartet auf Ihre Freigabe`}
                sub={`${fmtEur(inv.total)} brutto · vom ${fmtDateShort(inv.invoice_date)}${inv.adjustable ? " · Betrag anpassbar" : ""}`}
                actions={
                  <>
                    {inv.adjustable && (
                      <Link to="/lex/honorar" className="text-xs text-slate-500 hover:text-slate-800 underline underline-offset-4 pb-2.5"
                        data-testid={`lex-todo-adjust-${inv.invoice_number}`}>
                        Betrag anpassen
                      </Link>
                    )}
                    <button onClick={() => sendHonorar(inv)} disabled={busy === `send-${inv.id}`} className="lex-btn text-xs"
                      data-testid={`lex-todo-send-${inv.invoice_number}`}>
                      {busy === `send-${inv.id}` ? "Sende …" : "Freigeben & senden"}
                    </button>
                  </>
                }
              />
            ))}

            {unpaid.map((inv) => {
              const overdue = inv.due_date && new Date(inv.due_date) < new Date(new Date().toDateString());
              const overdueDays = overdue ? Math.floor((new Date() - new Date(inv.due_date)) / 86400000) : 0;
              return (
              <TodoRow
                key={inv.id}
                testid={`lex-todo-unpaid-${inv.invoice_number}`}
                icon={<FileText className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: overdue ? "#B91C1C" : "#C5A059" }} />}
                title={`Honorarnote ${inv.invoice_number} noch unbezahlt`}
                sub={
                  <>
                    {fmtEur(inv.total)} brutto
                    {inv.due_date && <> · zahlbar bis <span className="lex-mono">{fmtDateShort(inv.due_date)}</span></>}
                    {overdue && (
                      <span className="font-semibold uppercase tracking-wider ml-1" style={{ color: "#B91C1C" }}
                        data-testid={`lex-todo-overdue-${inv.invoice_number}`}>
                        · überfällig{overdueDays > 0 ? ` seit ${overdueDays} Tag${overdueDays === 1 ? "" : "en"}` : ""}
                      </span>
                    )}
                  </>
                }
                actions={
                  paidForId === inv.id ? (
                    <>
                      <div>
                        <label className="block text-[11px] uppercase tracking-widest font-semibold text-slate-400 mb-1">Bezahlt am</label>
                        <input type="date" className="lex-input lex-mono text-sm py-1.5" value={invPaidDate}
                          max={today()} onChange={(e) => setInvPaidDate(e.target.value)}
                          data-testid={`lex-todo-paid-date-${inv.invoice_number}`} />
                      </div>
                      <button onClick={() => markInvoicePaid(inv)} disabled={busy === `paid-${inv.id}`} className="lex-btn text-xs"
                        data-testid={`lex-todo-paid-confirm-${inv.invoice_number}`}>
                        Bestätigen ✓
                      </button>
                      <button onClick={() => setPaidForId(null)}
                        className="text-xs text-slate-400 hover:text-slate-600 underline underline-offset-4 pb-2.5">
                        Abbrechen
                      </button>
                    </>
                  ) : (
                    <>
                      {overdue && (
                        <div className="relative">
                          <button onClick={() => remindKanzlei(inv)} disabled={busy === `remind-${inv.id}`}
                            className="lex-btn-outline text-xs"
                            data-testid={`lex-todo-remind-btn-${inv.invoice_number}`}>
                            {busy === `remind-${inv.id}` ? "Sende …" : "Kanzlei erinnern"}
                          </button>
                          {inv.lex_kanzlei_reminder_at && (
                            <p className="absolute top-full left-1/2 -translate-x-1/2 mt-1 text-[10px] text-slate-400 lex-mono whitespace-nowrap"
                              data-testid={`lex-todo-reminded-at-${inv.invoice_number}`}>
                              Erinnert am {fmtDateShort(inv.lex_kanzlei_reminder_at)}
                            </p>
                          )}
                        </div>
                      )}
                      <button onClick={() => { setPaidForId(inv.id); setInvPaidDate(today()); }} className="lex-btn-outline text-xs"
                        data-testid={`lex-todo-paid-btn-${inv.invoice_number}`}>
                        Als bezahlt markieren ✓
                      </button>
                    </>
                  )
                }
              />
            );})}
          </div>
        )}
      </div>

      {/* Wo Sie stehen — Jahres-Kennzahlen */}
      <div className="mb-10 lex-fade-up-2" data-testid="lex-dash-stats">
        <p className="text-xs uppercase tracking-widest font-semibold mb-4" style={{ color: "#C5A059" }}>Wo Sie stehen · {stats.year}</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatTile label={`Honorare ${stats.year}`} value={fmtEur(stats.total)} sub={`${stats.count || 0} Note${stats.count === 1 ? "" : "n"} · brutto`} testid="lex-stat-total" />
          <StatTile label="Davon offen" value={fmtEur(stats.open)} sub={stats.open > 0 ? "Zahlung ausständig" : "alles bezahlt ✓"} testid="lex-stat-open" />
          <StatTile label="Nächste Honorarnote" value={fmtDateShort(rec?.next_run_date)} sub={rec ? `${fmtEur(rec.total)} brutto` : "—"} testid="lex-stat-next" />
          <StatTile label="Letzte UVA" value={lastUva ? lastUva.period_label : "—"} sub={lastUva ? uvaStatusLabel(lastUva) : "noch keine freigegeben"} testid="lex-stat-uva" />
        </div>
      </div>

      {/* Einheitliche Status-Boxen */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
        <StatusBox
          label="Monatliche Honorarnote" icon={FileText} fade="lex-fade-up-1" testid="lex-widget-honorarnote"
          value={rec
            ? <p className="lex-mono text-3xl font-semibold" style={{ color: "#0A1128" }}>{fmtEur(rec.total)}</p>
            : <p className="text-sm text-slate-500 pt-2">Noch keine Honorarnote eingerichtet.</p>}
          sub={rec ? <>brutto / Monat an <span className="font-medium text-slate-700">{rec.customer_name}</span></> : null}
          rows={[
            ["Nächste Note", <span className="lex-mono" data-testid="lex-next-run-date">{fmtDate(rec?.next_run_date)}</span>],
            ["Erstellt bisher", <span className="lex-mono">{rec?.generated_count ?? "—"}</span>],
            ["Modus", <span className="lex-mono uppercase">{honorar.mode || "fix"}</span>],
          ]}
          action={
            <Link to="/lex/honorar" className="lex-btn-outline inline-block text-center w-full" data-testid="lex-honorarnote-link">
              Honorarnoten verwalten
            </Link>
          }
        />

        <StatusBox
          label="Umsatzsteuer (UVA)" icon={ReceiptText} fade="lex-fade-up-2" testid="lex-widget-uva"
          value={
            <div className="flex items-center gap-2 flex-wrap">
              <span className="lex-mono text-3xl font-semibold" style={{ color: "#0A1128" }}>
                {lastUva ? lastUva.period_label : "—"}
              </span>
              {lastUva && (
                <span className="text-xs px-2 py-1 uppercase tracking-wider font-semibold" style={uvaChipStyle(lastUva)} data-testid="lex-uva-status">
                  {uvaStatusLabel(lastUva)}
                </span>
              )}
            </div>
          }
          sub="Berechnung, Prüfung und Einreichung übernimmt Ihre Kanzlei."
          rows={[
            ["Besteuerung", <span className="lex-mono">{status?.tax_scheme === "cash_basis" ? "Ist (vereinnahmt)" : "Soll"}</span>],
            ["Vorsteuerpauschale", <span className="lex-mono" data-testid="lex-vstp-badge">{status?.vstp?.enabled ? "1,8 % aktiv ✓" : "Inaktiv"}</span>],
            ["Steuernummer", <span className="lex-mono">{status?.tax_number || "—"}</span>],
          ]}
          action={
            <Link to="/voranmeldung" className="lex-btn-outline inline-block text-center w-full" data-testid="lex-uva-link">
              Voranmeldungen
            </Link>
          }
        />

        <StatusBox
          label="Ihre Steuerberatung" icon={ShieldCheck} dark fade="lex-fade-up-3" testid="lex-widget-zero-effort"
          value={<p className="lex-serif text-2xl font-semibold leading-snug">Alles in Betreuung.</p>}
          sub="UVA, Pauschalierung, Fristen und Erklärungen laufen vollständig über Ihre Steuerberatungs-GmbH."
          rows={[["stb_vollmacht", "Steuerberater-Vollmacht"], ["finanzonline", "FinanzOnline-Vollmacht"], ["bank_consent", "Bank-Consent (PSD2)"]].map(([key, label]) => [
            <span key={`l-${key}`} className={vm[key] ? "text-slate-200" : "text-slate-500"}>{label}</span>,
            <span key={`v-${key}`} data-testid={`lex-vollmacht-status-${key}`}>
              {vm[key]
                ? <Check className="h-4 w-4" style={{ color: "#34d399" }} />
                : <Minus className="h-4 w-4 text-slate-600" />}
            </span>,
          ])}
          action={
            <button onClick={() => window.dispatchEvent(new CustomEvent("lex-open-kanzlei-contact"))}
              className="w-full text-center text-xs uppercase tracking-widest font-semibold py-3 border transition-colors hover:bg-white/5"
              style={{ borderColor: "#C5A059", color: "#C5A059" }} data-testid="lex-kanzlei-contact-btn">
              Nachricht an die Kanzlei
            </button>
          }
        />
      </div>

      {/* UVA-Verlauf */}
      {history.length > 0 && (
        <div className="mt-10 lex-fade-up-3" data-testid="lex-uva-history">
          <p className="text-xs uppercase tracking-widest font-semibold mb-4" style={{ color: "#C5A059" }}>UVA-Verlauf</p>
          <div className="flex flex-wrap gap-2">
            {[...history].reverse().map((r) => (
              <span key={r.period}
                className="lex-mono text-xs px-2.5 py-1.5"
                style={uvaChipStyle(r)}
                title={`${r.period_label}: ${uvaStatusLabel(r)}${r.status === "paid" && r.paid_at ? ` am ${fmtDateShort(r.paid_at)}` : r.zahllast > 0 ? ` · ${fmtEur(r.zahllast)} fällig ${fmtDateShort(r.due_date)}` : ""}`}
                data-testid={`lex-uva-history-chip-${r.period}`}>
                {uvaChipMark(r)} {r.period_label}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
